import React, { useMemo } from 'react';
import { get } from 'lodash';
import {
  BeneficialOwnerType,
  CompanyApplicantRelatedPartyType,
  TrustApplicantRelatedPartyType,
} from '@lendi/core-constants';
import { Accordion, AccordionGroup } from '@lendi-ui/accordion';
import Grid from '@lendi-ui/grid';
import { Heading } from '@lendi-ui/typography';

import { CompanyContactType, FIELD_PATHS, PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { ApplicantEntityIndex, FormValuesApplicants, RelatedParty, StoreApplicant } from '@funnels/types';
import {
  capitalizeForEnumValue,
  getApplicantName,
  getDisplayDateString,
  mapFieldNamesForCompanyContactType,
} from '@funnels/utils';

import { StringDisplay, StringsDisplay } from '../../../Fields';
import { GreyedPanelText } from '../../../Fields/PlainDisplay/style';
import { AccordionContent, AccordionHeaderCircleIcon, AccordionWrapper, PaddedGridUnit } from '../_common/style';
import { Link, LinkContainer } from './layouts.style';

interface CompanyApplicantSummaryLayoutProps extends ApplicantEntityIndex {
  goToSection: (clientId: string, section: string) => void;
  applicantIndex: number;
  formValues: FormValuesApplicants;
}

const CompanyDirectors = ({
  director,
  relatedParties,
  currentApplicantClientId,
  applicants,
  index,
}: {
  director: RelatedParty;
  relatedParties: RelatedParty[];
  currentApplicantClientId: string;
  applicants: StoreApplicant[];
  index: number;
}) => {
  const directorApplicantId = get({ relatedParties: [director] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0));

  const companyBeneficialOwner = relatedParties.find(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === directorApplicantId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_COMPANY_APPLICANT_ID(0)) ===
        currentApplicantClientId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        CompanyApplicantRelatedPartyType.CompanyBeneficialOwner
  );

  const beneficialOwnerRole = get(
    { relatedParties: [companyBeneficialOwner] },
    FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(0)
  );
  const beneficialOwnerType = get(
    { relatedParties: [companyBeneficialOwner] },
    FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(0)
  );

  const beneficialDescription = get(
    { relatedParties: [companyBeneficialOwner] },
    FIELD_PATHS.RELATED_PARTY_DESCRIPTION(0)
  );
  const beneficialOwnerPercentage = get(
    { relatedParties: [companyBeneficialOwner] },
    FIELD_PATHS.RELATED_PARTY_COMPANY_BENEFICIAL_OWNER_PERCENTAGE(0),
    0
  );

  const heading = getApplicantName(applicants, directorApplicantId) || `Director ${index + 1}`;

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1}>
        <Heading size="xxs">{heading}</Heading>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Beneficial owner" value={companyBeneficialOwner ? 'Yes' : 'No'} />
      </PaddedGridUnit>
      {!!companyBeneficialOwner && (
        <>
          <PaddedGridUnit size={1 / 2}>
            <StringDisplay title="Beneficial owner type" value={capitalizeForEnumValue(beneficialOwnerType, true)} />
          </PaddedGridUnit>
          {beneficialOwnerType === BeneficialOwnerType.Controller && (
            <PaddedGridUnit size={1 / 2}>
              <StringDisplay title="Beneficial owner role" value={capitalizeForEnumValue(beneficialOwnerRole)} />
            </PaddedGridUnit>
          )}
          {beneficialOwnerType === BeneficialOwnerType.Other && (
            <PaddedGridUnit size={1 / 2}>
              <StringDisplay title="Description" value={beneficialDescription} />
            </PaddedGridUnit>
          )}
          <PaddedGridUnit size={1 / 2}>
            <StringDisplay title="Beneficial ownership percentage" value={`${beneficialOwnerPercentage * 100}%`} />
          </PaddedGridUnit>
        </>
      )}
    </Grid>
  );
};

export const CompanyApplicantSummaryLayout = (props: CompanyApplicantSummaryLayoutProps) => {
  const { tracking } = useAnalytics();
  const { applicantIndex, goToSection, formValues } = props;
  const [opened, setOpened] = React.useState<boolean[]>([true, true, true]);

  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const currentApplicantClientId = get(formValues, FIELD_PATHS.APPLICANT_CLIENT_ID(applicantIndex));

  const companyName = get(formValues, FIELD_PATHS.APPLICANT_COMPANY_NAME(applicantIndex));
  const companyType = capitalizeForEnumValue(get(formValues, FIELD_PATHS.APPLICANT_COMPANY_TYPE(applicantIndex)));
  const companyStructure = capitalizeForEnumValue(
    get(formValues, FIELD_PATHS.APPLICANT_COMPANY_STRUCTURE(applicantIndex))
  );
  const registeredDateString = getDisplayDateString(
    get(formValues, FIELD_PATHS.APPLICANT_COMPANY_REGISTERED_DATE(applicantIndex))
  );
  const companyIsNotRegisteredForGST = get(
    formValues,
    FIELD_PATHS.APPLICANT_COMPANY_NOT_REGISTERED__FOR_GST(applicantIndex)
  );
  const companyRegisteredState = get(formValues, FIELD_PATHS.APPLICANT_COMPANY_REGISTERED_STATE(applicantIndex));
  const companyIndustryCode = get(formValues, FIELD_PATHS.APPLICANT_COMPANY_INDUSTRY_CODE(applicantIndex));
  const companyABN = get(formValues, FIELD_PATHS.APPLICANT_COMPANY_ABN(applicantIndex));
  const companyACN = get(formValues, FIELD_PATHS.APPLICANT_COMPANY_ACN(applicantIndex));
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const trustees = relatedParties.filter(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        TrustApplicantRelatedPartyType.Trustee &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === currentApplicantClientId
  );
  const trusteeIds = trustees.map((trustee) =>
    get({ relatedParties: [trustee] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_ID(0))
  );
  const trustApplicants = applicants
    .filter((applicant) => trusteeIds.includes(get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ID(0))))
    .map((applicant) => get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TRUST_NAME(0)));

  const directors = relatedParties.filter(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        CompanyApplicantRelatedPartyType.Director &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_COMPANY_APPLICANT_ID(0)) ===
        currentApplicantClientId
  );

  let companyContact = relatedParties.find(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_COMPANY_APPLICANT_ID(0)) ===
        currentApplicantClientId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        CompanyApplicantRelatedPartyType.CompanyContact
  );
  const NPCompanyContactApplicantId = get(
    { relatedParties: [companyContact] },
    FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)
  );
  companyContact = NPCompanyContactApplicantId
    ? relatedParties.find(
        (relatedParty) =>
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) ===
            NPCompanyContactApplicantId &&
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
            CompanyApplicantRelatedPartyType.CompanyContact
      )
    : companyContact;

  const {
    companyContactTitle,
    companyContactFirstName,
    companyContactLastName,
    companyContactRegisteredAddress,
    companyContactTradingAddress,
    companyContactMailingAddress,
    companyContactNumber,
    companyContactEmail,
  } = useMemo(() => {
    switch (companyContact?.companyContactType) {
      case CompanyContactType.NonApplicant:
        const { [CompanyContactType.NonApplicant]: nonApplicantPaths } = mapFieldNamesForCompanyContactType(0);
        return {
          companyContactTitle: get({ relatedParties: [companyContact] }, nonApplicantPaths.titlePath),
          companyContactFirstName: get({ relatedParties: [companyContact] }, nonApplicantPaths.firstNamePath),
          companyContactLastName: get({ relatedParties: [companyContact] }, nonApplicantPaths.lastNamePath),
          companyContactRegisteredAddress: get(
            { relatedParties: [companyContact] },
            nonApplicantPaths.registeredAddressPathFormatted
          ),
          companyContactTradingAddress: get(
            { relatedParties: [companyContact] },
            nonApplicantPaths.tradingAddressPathFormatted
          ),
          companyContactMailingAddress: get(
            { relatedParties: [companyContact] },
            nonApplicantPaths.mailingAddressPathFormatted
          ),
          companyContactNumber: get({ relatedParties: [companyContact] }, nonApplicantPaths.contactNumberPath),
          companyContactEmail: get({ relatedParties: [companyContact] }, nonApplicantPaths.contactEmailPath),
        };

      case CompanyContactType.Applicant:
        const applicantIndex = applicants.findIndex(({ id }) => id === companyContact?.applicantId);
        const { [CompanyContactType.Applicant]: applicantPaths } = mapFieldNamesForCompanyContactType(applicantIndex);
        return {
          companyContactTitle: get({ applicants }, applicantPaths.titlePath),
          companyContactFirstName: get({ applicants }, applicantPaths.firstNamePath),
          companyContactLastName: get({ applicants }, applicantPaths.lastNamePath),
          companyContactRegisteredAddress: get({ applicants }, applicantPaths.registeredAddressPathFormatted),
          companyContactTradingAddress: get({ applicants }, applicantPaths.tradingAddressPathFormatted),
          companyContactMailingAddress: get({ applicants }, applicantPaths.mailingAddressPathFormatted),
          companyContactNumber: get({ applicants }, applicantPaths.contactNumberPath),
          companyContactEmail: get({ applicants }, applicantPaths.contactEmailPath),
        };
      default:
        return {};
    }
  }, [applicants, companyContact]);

  const isCompanyInformationCompleted = useMemo(() => {
    const isActingOnBehalfOfATrust =
      !!trustees.length &&
      trustees.every(
        (relatedParty) => !!get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_ID(0))
      );

    const isCompanyAbnSufficient = isActingOnBehalfOfATrust || companyIsNotRegisteredForGST || !!companyABN;

    return (
      !!companyStructure &&
      !!registeredDateString &&
      !!companyRegisteredState &&
      !!companyIndustryCode &&
      !!companyACN &&
      isCompanyAbnSufficient
    );
  }, [
    trustees,
    companyStructure,
    registeredDateString,
    companyRegisteredState,
    companyIndustryCode,
    companyABN,
    companyACN,
    companyIsNotRegisteredForGST,
  ]);

  const isDirectorDetailsCompleted = useMemo(() => {
    let hasOneControllerType = false;
    for (const director of directors) {
      const directorApplicantId = get({ relatedParties: [director] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0));
      const companyBeneficialOwner = relatedParties.find(
        (relatedParty) =>
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === directorApplicantId &&
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_COMPANY_APPLICANT_ID(0)) ===
            currentApplicantClientId &&
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
            CompanyApplicantRelatedPartyType.CompanyBeneficialOwner
      );

      const beneficialOwnerRole = get(
        { relatedParties: [companyBeneficialOwner] },
        FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(0)
      );
      const beneficialOwnerType = get(
        { relatedParties: [companyBeneficialOwner] },
        FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(0)
      );
      const description = get({ relatedParties: [companyBeneficialOwner] }, FIELD_PATHS.RELATED_PARTY_DESCRIPTION(0));

      // spec doc https://creditandfinance.atlassian.net/wiki/spaces/Product/pages/2722693434/Non+Person+Applicant+Data+Requirements?focusedCommentId=2807627831#:~:text=Free%20Text-,Conditionally%20Required,-IF%20Beneficial%20Owner
      if (
        (!!companyBeneficialOwner && !beneficialOwnerType) ||
        (beneficialOwnerType === BeneficialOwnerType.Controller && !beneficialOwnerRole) ||
        (beneficialOwnerType === BeneficialOwnerType.Other && !description)
      ) {
        return false;
      }

      if (beneficialOwnerType === BeneficialOwnerType.Controller) {
        hasOneControllerType = true;
      }
    }
    // for the company to be complete, it must have at least 1 controller director on the application
    return hasOneControllerType && directors.length > 0;
  }, [directors, relatedParties, currentApplicantClientId]);

  const isCompanyContactDetailsCompleted = useMemo(
    () =>
      !!companyContactTitle &&
      !!companyContactFirstName &&
      !!companyContactLastName &&
      !!companyContactRegisteredAddress &&
      !!companyContactTradingAddress &&
      !!companyContactMailingAddress &&
      !!companyContactNumber &&
      !!companyContactEmail,
    [
      companyContactTitle,
      companyContactFirstName,
      companyContactLastName,
      companyContactRegisteredAddress,
      companyContactTradingAddress,
      companyContactMailingAddress,
      companyContactNumber,
      companyContactEmail,
    ]
  );

  function handleAccordionClick(index: number, title: string) {
    const arr = [...opened];
    tracking?.accordion[arr[index] ? 'collapsed' : 'expanded']({ name: title, position: title });
    arr[index] = !arr[index]; // toggle
    setOpened(arr);
  }

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1}>
        <GreyedPanelText>Company details</GreyedPanelText>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Company name" value={companyName} className={PII_FIELD_MASKING_CLASS} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Company type" value={companyType} />
      </PaddedGridUnit>
      <AccordionWrapper>
        <AccordionGroup>
          {/* Company information */}
          <Accordion isOpen={opened[0]}>
            <Accordion.Header
              after={
                <AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isCompanyInformationCompleted} />
              }
              onClick={() => handleAccordionClick(0, 'Company information')}
              data-component="accordion-header"
            >
              <Heading size="xxs">Company information</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Company structure" value={companyStructure} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Company registered date" value={registeredDateString} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Company registered state" value={companyRegisteredState} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Industry code" value={companyIndustryCode} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="ABN" value={companyABN} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="ACN" value={companyACN} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Acting on behalf of trust" value={!!trustees.length ? 'Yes' : 'No'} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringsDisplay title="Trust applicants" values={trustApplicants} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(currentApplicantClientId, 'edit')}>View all or edit</Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>

          {/* Director details */}
          <Accordion isOpen={opened[1]}>
            <Accordion.Header
              after={<AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isDirectorDetailsCompleted} />}
              onClick={() => handleAccordionClick(1, 'Director details')}
              data-component="accordion-header"
            >
              <Heading size="xxs">Director details</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                {directors.map((director: RelatedParty, index) => (
                  <CompanyDirectors
                    key={`directors-${director.clientId}`}
                    director={director}
                    relatedParties={relatedParties}
                    currentApplicantClientId={currentApplicantClientId}
                    applicants={applicants}
                    index={index}
                  />
                ))}
                <LinkContainer>
                  <Link onClick={() => goToSection(currentApplicantClientId, 'edit')}>View all or edit</Link>
                </LinkContainer>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>

          {/* Company contact details */}
          <Accordion isOpen={opened[2]}>
            <Accordion.Header
              after={
                <AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isCompanyContactDetailsCompleted} />
              }
              onClick={() => handleAccordionClick(2, 'Company contact details')}
              data-component="accordion-header"
            >
              <Heading size="xxs">Company contact details</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Title" value={companyContactTitle} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="First name" value={companyContactFirstName} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Last name" value={companyContactLastName} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Registered address" value={companyContactRegisteredAddress} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Trading address" value={companyContactTradingAddress} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Mailing address" value={companyContactMailingAddress} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Number" value={companyContactNumber} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="E-mail" value={companyContactEmail} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(currentApplicantClientId, 'contact-details')}>
                        View all or edit
                      </Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>
        </AccordionGroup>
      </AccordionWrapper>
    </Grid>
  );
};
